import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    mdiMapMarkerOutline,
    mdiCogOutline,
    mdiCheckCircle,
    mdiCloseCircleOutline,
    mdiClipboardCheckOutline,
    mdiAlertOctagonOutline,
    mdiPlus
} from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Checkbox } from '../ui/checkbox'
import { Separator } from '../ui/separator'
import { useFormatters } from '../../lib/format'
import {
    useWorkOrderPortal,
    useWorkOrderEquipmentOptions,
    useAcceptWorkOrder,
    useDeclineWorkOrder,
    useSubmitWorkOrder,
    useUploadWorkOrderDocument,
    useDeclareEquipment,
    type WorkOrderPortalView,
} from '../../hooks/useWorkOrder'
import { EmptyState } from '../ui/empty-state'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * WorkOrderPortal is the page a contractor lands on from the link in an ordre
 * de service. Design constraints that shaped it:
 *
 *  - Mobile first and one column. It is read standing in a boiler room.
 *  - No app chrome, no navigation, no org context. The capability opens one
 *    work order; there is nowhere else to go.
 *  - Every state is stated plainly, including the ones we would rather not
 *    show: a dispute is echoed back with its reason, and a declared equipment
 *    says "awaiting confirmation" rather than implying the registry changed.
 */
export function WorkOrderPortal({ orgCode, token }: { orgCode: string; token: string }) {
    const { t } = useTranslation('workorders')
    const { data: view, isLoading, error } = useWorkOrderPortal(orgCode, token)

    if (isLoading) {
        return (
            <div className="mx-auto max-w-xl p-6 text-muted-foreground" data-testid="wo-loading">
                {t('portal.loading')}
            </div>
        )
    }

    // Every failure mode — unknown org, wrong/revoked/expired token, deleted
    // intervention — arrives here identically, by design.
    if (error || !view) {
        return (
            <div className="mx-auto max-w-xl p-6" data-testid="wo-not-found">
                <h1 className="text-xl font-semibold">{t('portal.not_found_title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t('portal.not_found_body')}</p>
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-xl space-y-6 p-4 pb-16 md:p-6" data-testid="wo-portal">
            <Header view={view} />
            <StatusBanner view={view} />
            <Separator />
            {!view.os_response && <AcceptOrDecline orgCode={orgCode} token={token} />}
            {view.os_response === 'accepted' && view.validation_status !== 'approved' && (
                <SubmitSection orgCode={orgCode} token={token} view={view} />
            )}
            <DocumentsSection orgCode={orgCode} token={token} view={view} />
        </div>
    )
}

function Header({ view }: { view: WorkOrderPortalView }) {
    const { t } = useTranslation('workorders')
    return (
        <header className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {view.organization_name} · {t('portal.work_order')} {view.reference}
            </p>
            <h1 className="text-2xl font-semibold leading-tight" data-testid="wo-title">
                {view.title}
            </h1>
            {view.asset_address || view.asset_name ? (
                <p className="flex items-start gap-1.5 text-sm text-muted-foreground" data-testid="wo-address">
                    <AppIcon path={mdiMapMarkerOutline} size="sm" className="mt-0.5 shrink-0" />
                    <span>{[view.asset_name, view.asset_address].filter(Boolean).join(' — ')}</span>
                </p>
            ) : null}
            {view.description ? (
                <p className="whitespace-pre-wrap pt-2 text-sm" data-testid="wo-description">
                    {view.description}
                </p>
            ) : null}
            {view.contract_reference || view.order_ref ? (
                <p className="pt-1 text-xs text-muted-foreground">
                    {[
                        view.contract_reference && `${t('portal.contract')} ${view.contract_reference}`,
                        view.order_ref && `${t('portal.order_ref')} ${view.order_ref}`,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </p>
            ) : null}
        </header>
    )
}

/** StatusBanner says where the job stands in one sentence. */
function StatusBanner({ view }: { view: WorkOrderPortalView }) {
    const { t } = useTranslation('workorders')
    const { formatDate } = useFormatters()
    const fmt = (s?: string | null) => (s ? formatDate(s, { dateStyle: 'long', timeStyle: 'short' }) : '')

    if (view.validation_status === 'approved') {
        return <Banner tone="ok" icon={mdiCheckCircle} testid="wo-status-approved" text={t('portal.status.approved')} />
    }
    if (view.validation_status === 'disputed') {
        return (
            <Banner
                tone="warn"
                icon={mdiAlertOctagonOutline}
                testid="wo-status-disputed"
                text={t('portal.status.disputed', { reason: view.dispute_reason })}
            />
        )
    }
    if (view.work_submitted_at) {
        return (
            <Banner
                tone="ok"
                icon={mdiClipboardCheckOutline}
                testid="wo-status-submitted"
                text={t('portal.status.submitted', { date: fmt(view.work_submitted_at) })}
            />
        )
    }
    if (view.os_response === 'declined') {
        return (
            <Banner
                tone="warn"
                icon={mdiCloseCircleOutline}
                testid="wo-status-declined"
                text={t('portal.status.declined', { reason: view.decline_reason })}
            />
        )
    }
    if (view.os_response === 'accepted') {
        return (
            <Banner
                tone="ok"
                icon={mdiCheckCircle}
                testid="wo-status-accepted"
                text={t('portal.status.accepted', { date: fmt(view.scheduled_at) })}
            />
        )
    }
    return <Banner tone="info" icon={SECTION_ICONS.documents} testid="wo-status-awaiting" text={t('portal.status.awaiting')} />
}

function Banner({
    tone,
    icon,
    text,
    testid,
}: {
    tone: 'ok' | 'warn' | 'info'
    icon: string
    text: string
    testid: string
}) {
    const toneClass =
        tone === 'ok'
            ? 'border-emerald-500/40 bg-emerald-500/10'
            : tone === 'warn'
              ? 'border-destructive/40 bg-destructive/10'
              : 'border-border bg-muted/40'
    return (
        <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${toneClass}`} data-testid={testid}>
            <AppIcon path={icon} size="md" className="mt-0.5 shrink-0" />
            <span>{text}</span>
        </div>
    )
}

/**
 * AcceptOrDecline is the only screen that matters commercially: it is where a
 * date gets committed. The date field is required — an acknowledgement without
 * one is exactly the useless "well received" this loop replaces.
 */
function AcceptOrDecline({ orgCode, token }: { orgCode: string; token: string }) {
    const { t } = useTranslation('workorders')
    const accept = useAcceptWorkOrder(orgCode, token)
    const decline = useDeclineWorkOrder(orgCode, token)

    const [when, setWhen] = useState('')
    const [note, setNote] = useState('')
    const [declining, setDeclining] = useState(false)
    const [reason, setReason] = useState('')
    const [err, setErr] = useState<string | null>(null)

    const submitAccept = () => {
        setErr(null)
        if (!when) {
            setErr(t('portal.errors.date_required'))
            return
        }
        accept.mutate(
            { scheduled_at: new Date(when).toISOString(), note: note || undefined },
            { onError: (e: unknown) => setErr(errorMessage(e, t('portal.errors.generic'))) },
        )
    }

    const submitDecline = () => {
        setErr(null)
        if (!reason.trim()) {
            setErr(t('portal.errors.reason_required'))
            return
        }
        decline.mutate({ reason }, { onError: (e: unknown) => setErr(errorMessage(e, t('portal.errors.generic'))) })
    }

    return (
        <section className="space-y-4" data-testid="wo-respond">
            <h2 className="text-base font-semibold">{t('portal.respond_title')}</h2>

            {!declining ? (
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="wo-date">{t('portal.date_label')}</Label>
                        <Input
                            id="wo-date"
                            type="datetime-local"
                            value={when}
                            onChange={(e) => setWhen(e.target.value)}
                            data-testid="wo-date-input"
                        />
                        <p className="text-xs text-muted-foreground">{t('portal.date_hint')}</p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="wo-note">{t('portal.note_label')}</Label>
                        <Textarea
                            id="wo-note"
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            data-testid="wo-note-input"
                        />
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            className="flex-1"
                            onClick={submitAccept}
                            disabled={accept.isPending}
                            data-testid="wo-accept-btn"
                        >
                            {t('portal.accept')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setDeclining(true)}
                            data-testid="wo-decline-open-btn"
                        >
                            {t('portal.decline')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="wo-reason">{t('portal.reason_label')}</Label>
                        <Textarea
                            id="wo-reason"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            data-testid="wo-reason-input"
                        />
                        <p className="text-xs text-muted-foreground">{t('portal.reason_hint')}</p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={submitDecline}
                            disabled={decline.isPending}
                            data-testid="wo-decline-btn"
                        >
                            {t('portal.confirm_decline')}
                        </Button>
                        <Button variant="ghost" onClick={() => setDeclining(false)} data-testid="wo-decline-cancel-btn">
                            {t('portal.cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {err ? (
                <p className="text-sm text-destructive" data-testid="wo-error">
                    {err}
                </p>
            ) : null}
        </section>
    )
}

/**
 * SubmitSection is where the field survey happens: the technician says what
 * they actually touched, and — the part that makes each OS pay for itself —
 * declares the equipment the registry does not know about.
 */
function SubmitSection({
    orgCode,
    token,
    view,
}: {
    orgCode: string
    token: string
    view: WorkOrderPortalView
}) {
    const { t } = useTranslation('workorders')
    const submit = useSubmitWorkOrder(orgCode, token)
    const { data: options } = useWorkOrderEquipmentOptions(orgCode, token, true)

    const [selected, setSelected] = useState<string[]>([])
    const [note, setNote] = useState('')
    const [err, setErr] = useState<string | null>(null)

    const toggle = (uid: string) =>
        setSelected((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]))

    const doSubmit = () => {
        setErr(null)
        submit.mutate(
            { note: note || undefined, equipment_uids: selected.length ? selected : undefined },
            { onError: (e: unknown) => setErr(errorMessage(e, t('portal.errors.generic'))) },
        )
    }

    const alreadySubmitted = !!view.work_submitted_at

    return (
        <section className="space-y-4" data-testid="wo-submit-section">
            <h2 className="text-base font-semibold">{t('portal.submit_title')}</h2>

            <div className="space-y-2">
                <Label>{t('portal.equipment_label')}</Label>
                <p className="text-xs text-muted-foreground">
                    {options && !options.trade_filtered
                        ? t('portal.equipment_hint_unfiltered')
                        : t('portal.equipment_hint')}
                </p>
                <div className="space-y-1.5" data-testid="wo-equipment-list">
                    {(options?.existing ?? []).map((e) => (
                        <label
                            key={e.uid}
                            className="flex items-start gap-2 rounded-md border p-2 text-sm"
                            data-testid={`wo-equipment-${e.uid}`}
                        >
                            <Checkbox
                                checked={selected.includes(e.uid)}
                                onCheckedChange={() => toggle(e.uid)}
                                data-testid={`wo-equipment-check-${e.uid}`}
                            />
                            <span className="flex-1">
                                <span className="font-medium">{e.name || e.type_name}</span>
                                <span className="block text-xs text-muted-foreground">
                                    {[e.type_name, e.location].filter(Boolean).join(' · ')}
                                    {e.declared ? ` · ${t('portal.equipment_pending_short')}` : ''}
                                </span>
                            </span>
                        </label>
                    ))}
                    {options && options.existing.length === 0 ? (
                        <EmptyState
                            variant="inline"
                            className="py-3"
                            description={t('portal.equipment_empty')}
                            data-testid="wo-equipment-empty"
                        />
                    ) : null}
                </div>
                <DeclareEquipmentForm orgCode={orgCode} token={token} />
            </div>

            {view.equipment.length > 0 ? (
                <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="wo-equipment-reported">
                    <p className="mb-1 font-medium">{t('portal.equipment_reported')}</p>
                    <ul className="space-y-0.5">
                        {view.equipment.map((e) => (
                            <li key={e.uid} className="text-muted-foreground">
                                {e.name || e.type}
                                {e.pending ? ` — ${t('portal.equipment_pending')}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            <div className="space-y-1.5">
                <Label htmlFor="wo-report-note">{t('portal.report_note_label')}</Label>
                <Textarea
                    id="wo-report-note"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="wo-report-note-input"
                />
            </div>

            <Button onClick={doSubmit} disabled={submit.isPending} className="w-full" data-testid="wo-submit-btn">
                {alreadySubmitted ? t('portal.resubmit') : t('portal.submit')}
            </Button>

            {err ? (
                <p className="text-sm text-destructive" data-testid="wo-submit-error">
                    {err}
                </p>
            ) : null}
        </section>
    )
}

/**
 * DeclareEquipmentForm is three fields on purpose. Anything longer does not get
 * filled in on a phone, in a basement, with gloves on.
 */
function DeclareEquipmentForm({ orgCode, token }: { orgCode: string; token: string }) {
    const { t } = useTranslation('workorders')
    const { data: options } = useWorkOrderEquipmentOptions(orgCode, token, true)
    const declare = useDeclareEquipment(orgCode, token)

    const [open, setOpen] = useState(false)
    const [typeUid, setTypeUid] = useState('')
    const [name, setName] = useState('')
    const [plate, setPlate] = useState<File | null>(null)
    const [err, setErr] = useState<string | null>(null)

    const reset = () => {
        setTypeUid('')
        setName('')
        setPlate(null)
        setErr(null)
        setOpen(false)
    }

    const doDeclare = () => {
        setErr(null)
        if (!typeUid) {
            setErr(t('portal.errors.type_required'))
            return
        }
        if (!name.trim()) {
            setErr(t('portal.errors.name_required'))
            return
        }
        declare.mutate(
            { asset_type_uid: typeUid, name, plate },
            {
                onSuccess: reset,
                onError: (e: unknown) => setErr(errorMessage(e, t('portal.errors.generic'))),
            },
        )
    }

    if (!open) {
        return (
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="wo-declare-open-btn">
                <AppIcon path={mdiPlus} size="sm" className="mr-1" />
                {t('portal.declare_open')}
            </Button>
        )
    }

    return (
        <div className="space-y-3 rounded-md border p-3" data-testid="wo-declare-form">
            <p className="flex items-center gap-1.5 text-sm font-medium">
                <AppIcon path={mdiCogOutline} size="sm" />
                {t('portal.declare_title')}
            </p>
            <p className="text-xs text-muted-foreground">{t('portal.declare_hint')}</p>

            <div className="space-y-1.5">
                <Label htmlFor="wo-declare-type">{t('portal.declare_type')}</Label>
                <select
                    id="wo-declare-type"
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={typeUid}
                    onChange={(e) => setTypeUid(e.target.value)}
                    data-testid="wo-declare-type-select"
                >
                    <option value="">{t('portal.declare_type_placeholder')}</option>
                    {(options?.types ?? []).map((ty) => (
                        <option key={ty.uid} value={ty.uid}>
                            {ty.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="wo-declare-name">{t('portal.declare_name')}</Label>
                <Input
                    id="wo-declare-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('portal.declare_name_placeholder')}
                    data-testid="wo-declare-name-input"
                />
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="wo-declare-plate">{t('portal.declare_plate')}</Label>
                <Input
                    id="wo-declare-plate"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPlate(e.target.files?.[0] ?? null)}
                    data-testid="wo-declare-plate-input"
                />
                <p className="text-xs text-muted-foreground">{t('portal.declare_plate_hint')}</p>
            </div>

            <div className="flex gap-2">
                <Button size="sm" onClick={doDeclare} disabled={declare.isPending} data-testid="wo-declare-btn">
                    {t('portal.declare_submit')}
                </Button>
                <Button size="sm" variant="ghost" onClick={reset} data-testid="wo-declare-cancel-btn">
                    {t('portal.cancel')}
                </Button>
            </div>

            {err ? (
                <p className="text-sm text-destructive" data-testid="wo-declare-error">
                    {err}
                </p>
            ) : null}
        </div>
    )
}

function DocumentsSection({
    orgCode,
    token,
    view,
}: {
    orgCode: string
    token: string
    view: WorkOrderPortalView
}) {
    const { t } = useTranslation('workorders')
    const { formatDate } = useFormatters()
    const upload = useUploadWorkOrderDocument(orgCode, token)
    const [err, setErr] = useState<string | null>(null)

    if (view.os_response === 'declined') return null

    const onPick = (file?: File | null) => {
        if (!file) return
        setErr(null)
        upload.mutate(file, { onError: (e: unknown) => setErr(errorMessage(e, t('portal.errors.generic'))) })
    }

    return (
        <section className="space-y-3" data-testid="wo-documents-section">
            <h2 className="text-base font-semibold">{t('portal.documents_title')}</h2>
            <p className="text-xs text-muted-foreground">{t('portal.documents_hint')}</p>

            <Input
                type="file"
                onChange={(e) => onPick(e.target.files?.[0])}
                disabled={upload.isPending}
                data-testid="wo-upload-input"
            />

            {view.documents.length > 0 ? (
                <ul className="space-y-1 text-sm" data-testid="wo-documents-list">
                    {view.documents.map((d, i) => (
                        <li key={`${d.name}-${i}`} className="flex items-start gap-2">
                            <AppIcon path={SECTION_ICONS.documents} size="sm" className="mt-0.5 shrink-0" />
                            <span>
                                {d.name}
                                <span className="block text-xs text-muted-foreground">
                                    {formatDate(d.uploaded_at, { dateStyle: 'medium' })}
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            ) : null}

            {err ? (
                <p className="text-sm text-destructive" data-testid="wo-upload-error">
                    {err}
                </p>
            ) : null}
        </section>
    )
}

/** errorMessage pulls a human message out of the API error envelope. */
function errorMessage(e: unknown, fallback: string): string {
    if (e && typeof e === 'object') {
        const anyErr = e as { message?: string; error?: { message?: string } }
        return anyErr.error?.message || anyErr.message || fallback
    }
    return fallback
}
